import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@ncoder/ui';
import { cn } from '@/lib/utils';

/**
 * The headline-figures rail, shared by the Usage and Analysis tabs.
 *
 * Extracted from `OrgUsageSummaryPanel` when Analysis needed the same rail with different measures.
 * Sharing the component rather than copying it is what makes Analysis read as part of the panel
 * instead of a second dashboard with its own idea of what a stat looks like — and it means the two
 * tabs cannot drift apart later.
 *
 * Deliberately borderless per stat: the figures share one rail separated by hairlines. Giving each
 * its own bordered box would put three nested rectangles between the page and a number.
 */

export interface StatSpec {
  label: string;
  value: string;
  /** Qualifier under the value — which clock, which window. */
  hint?: string;
  /** `warn` is for a figure that means something is wrong, never for emphasis. */
  tone?: 'default' | 'warn';
}

const PARTIAL_SOURCE_LABEL: Record<string, string> = {
  customers: 'spend and budget figures',
  spend_logs: 'token counts',
};

/**
 * `spend_logs_capped` is not a failure and must not be worded as one.
 *
 * LiteLLM refuses to count past a row ceiling (10,000 at the time of writing) and says so. The
 * figures that come back are real but LOW — which is more dangerous than a zero, because nothing
 * about them looks wrong. It therefore gets its own sentence rather than joining the
 * "could not return" list, which would describe the wrong problem and suggest the wrong remedy.
 */
const CAPPED_SOURCE = 'spend_logs_capped' as const;

function Stat({ label, value, hint, tone = 'default' }: StatSpec) {
  return (
    <div className="min-w-0 px-4 py-3 first:pl-[var(--oa-gutter)] last:pr-[var(--oa-gutter)]">
      <dt className="oa-micro truncate">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums leading-none',
          tone === 'warn' ? 'text-warning-strong' : 'text-foreground',
        )}
      >
        {value}
      </dd>
      {hint && <p className="oa-meta mt-1 truncate">{hint}</p>}
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="px-4 py-3 first:pl-[var(--oa-gutter)] last:pr-[var(--oa-gutter)]">
      <div className="oa-skeleton h-2.5 w-20" />
      <div className="oa-skeleton mt-2 h-4 w-16" />
    </div>
  );
}

export interface OrgUsageStatRailProps {
  stats: StatSpec[];
  isLoading?: boolean;
  skeletonCount?: number;
  /**
   * Upstream sources the API reported as failed-and-zeroed. Non-empty means some figures below read
   * 0 without actually being 0 — which the reader has to be told, or they will act on a wrong number.
   */
  partialSources?: Array<'customers' | 'spend_logs' | 'spend_logs_capped'>;
  ariaLabel: string;
}

export function OrgUsageStatRail({
  stats,
  isLoading,
  skeletonCount = 4,
  partialSources = [],
  ariaLabel,
}: OrgUsageStatRailProps) {
  return (
    <section aria-label={ariaLabel}>
      {!isLoading && partialSources.includes(CAPPED_SOURCE) && (
        <Alert className="mb-3 border-warning/30 bg-warning/10 text-warning-strong">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <AlertTitle className="text-[13px]">Some figures below are under-reported</AlertTitle>
          <AlertDescription className="text-xs">
            This organization has more usage records than the usage service will count in one
            query, so spend and token figures are lower than the true totals. Narrow the reporting
            window for a complete picture of a shorter period.
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && partialSources.filter((s) => s !== CAPPED_SOURCE).length > 0 && (
        <Alert className="mb-3 border-warning/30 bg-warning/10 text-warning-strong">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <AlertTitle className="text-[13px]">Some figures below may be wrong</AlertTitle>
          <AlertDescription className="text-xs">
            The usage service could not return{' '}
            {partialSources
              .filter((source) => source !== CAPPED_SOURCE)
              .map((source) => PARTIAL_SOURCE_LABEL[source] ?? source)
              .join(' or ')}{' '}
            just now. Affected figures show as 0, which may not be their real value.
          </AlertDescription>
        </Alert>
      )}

      {/*
        `auto-fit` with a minimum track: as many across as fit, without a viewport breakpoint — the
        rail sits inside a resizable panel, so the window's width is not its width.

        The 136px floor is chosen so a phone-width panel gets TWO columns rather than one. At a 150px
        floor, five stats became five stacked rows and pushed the first chart most of a screen down,
        which on the Analysis tab meant scrolling past the summary to reach the content.
      */}
      <dl className="oa-panel grid divide-x divide-y divide-border/50 [grid-template-columns:repeat(auto-fit,minmax(136px,1fr))]">
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, index) => <StatSkeleton key={index} />)
          : stats.map((stat) => <Stat key={stat.label + stat.hint} {...stat} />)}
      </dl>
    </section>
  );
}
